import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { BaseWidgetProps, NoContent, WidgetWrapper } from "shared-ui";
import { Devices } from "@mui/icons-material";
import { FC } from "react";
import { Skeleton, useTheme } from "@mui/material";
import { usePurchasedAssetsHardwareByAge } from "../../features/assets-feature/hooks/purchased-assets/usePurchasedAssetsHardwareByAge";

type DataPoint = { key: string; value: number };
type ChartData = {
  labels: string[];
  datasets: Array<{
    label: string;
    data: DataPoint[];
    [key: string]: any;
  }>;
  [key: string]: any;
};

function fillInData(data: ChartData): ChartData {
  if (!data?.datasets || !Array.isArray(data.datasets[0].data)) return data;
  const sortedData = [...data.datasets[0].data].sort(
    (a, b) => Number(a.key) - Number(b.key),
  );
  const labels = sortedData.map((d) => d.key);
  return {
    ...data,
    labels,
    datasets: [
      {
        ...data.datasets[0],
        data: sortedData,
      },
    ],
  };
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

const chartOptions = (theme: any) => ({
  scales: {
    y: {
      min: 0,
      ticks: {
        maxTicksLimit: 7,
        color: theme.palette.text.primary,
      },
    },
    x: {
      ticks: {
        color: theme.palette.text.primary,
      },
    },
  },
  interaction: {
    mode: "index" as const,
    intersect: false,
  },
  parsing: {
    xAxisKey: "key",
    yAxisKey: "value",
  },
  plugins: {
    legend: {
      display: false,
    },
  },
});

const HardwareByAgeWidget: FC<BaseWidgetProps> = ({ pageLayoutWidget }) => {
  const theme = useTheme();
  const { data, isEmptyDataSet, isLoading, error } = usePurchasedAssetsHardwareByAge();
  const chartData = fillInData(data);

  function widgetContents() {
    if (isLoading) {
      return <Skeleton variant="rectangular" width="100%" height={300} />;
    }
    if (error) {
      return (
        <NoContent
          containerHeight={140}
          header="Unable to Load Data"
          body="There was an error loading hardware by age data."
          actionText=""
          icon={<Devices />}
        />
      );
    }
    if (isEmptyDataSet) {
      return (
        <NoContent
          containerHeight={140}
          header="No Purchased Hardware Found"
          body=""
          actionText=""
          icon={<Devices />}
        />
      );
    }
    return <Bar data={chartData} options={chartOptions(theme)} />;
  }

  return (
    <WidgetWrapper
      pageLayoutWidget={pageLayoutWidget}
      isEmpty={!isLoading}
    >
      {widgetContents()}
    </WidgetWrapper>
  );
};

export default HardwareByAgeWidget;
